import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle2, AtSign, Phone, Loader2 } from 'lucide-react';
import { API_BASE_URL as API } from '../config/api';



export default function ForgotPasswordPage() {
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Detect what type of identifier user is entering
  const getIdentifierType = (value) => {
    if (!value) return null;
    if (value.includes('@') && value.includes('.')) return 'email';
    if (value.startsWith('@')) return 'handle';
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 10) return 'phone';
    if (value.match(/^[a-z0-9_]+$/i)) return 'handle';
    return 'email';
  };

  const identifierType = getIdentifierType(identifier);

  const getPlaceholder = () => {
    return 'Email, @handle, or phone number';
  };

  const getIcon = () => {
    switch (identifierType) {
      case 'handle':
        return <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />;
      case 'phone':
        return <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />;
      default:
        return <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!identifier.trim()) {
      setError('Please enter your email, @handle, or phone number');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: identifier.trim() })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Even on error, show success to prevent enumeration
        // Only show error for server errors
        if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }
      }
      
      // Always show success to prevent account enumeration
      setSubmitted(true);
      toast.success('Check your email or phone for reset instructions');
    } catch (error) {
      console.error('Error:', error);
      setError(error.message || 'Something went wrong. Please try again.');
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card className="glass-card border-border p-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            
            <h1 className="text-2xl font-bold mb-3">Check Your Inbox</h1>
            <p className="text-muted-foreground mb-6">
              If an account exists for <strong className="text-foreground">{identifier}</strong>, 
              you'll receive a password reset link via email
              {identifierType === 'phone' && ' and SMS'}.
            </p>
            
            <div className="bg-muted/30 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-muted-foreground mb-2">
                <strong className="text-foreground">Didn't get it?</strong>
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Check your spam/junk folder</li>
                <li>• Make sure you entered the correct info</li>
                <li>• Wait a few minutes and try again</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSubmitted(false);
                  setIdentifier('');
                }}
                className="w-full"
                data-testid="try-another-btn"
              >
                Try another email or phone
              </Button>
            </div>
            
            <div className="mt-6 pt-6 border-t border-border">
              <Link 
                to="/login" 
                className="text-sm text-secondary hover:underline inline-flex items-center gap-1"
                data-testid="back-to-signin-link"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Forgot Password?</h1>
          <p className="text-muted-foreground">
            Enter your email, @handle, or phone number and we'll send you a reset link
          </p>
        </div>

        <Card className="glass-card border-border p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="identifier">Email, @Handle, or Phone</Label>
              <div className="relative">
                {getIcon()}
                <Input
                  id="identifier"
                  type="text"
                  placeholder={getPlaceholder()}
                  value={identifier}
                  onChange={(e) => {
                    setIdentifier(e.target.value);
                    setError('');
                  }}
                  className="pl-10"
                  required
                  autoComplete="username"
                  data-testid="forgot-identifier-input"
                />
              </div>
              {identifierType && identifier && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  {identifierType === 'email' && <Mail className="w-3 h-3" />}
                  {identifierType === 'handle' && <AtSign className="w-3 h-3" />}
                  {identifierType === 'phone' && <Phone className="w-3 h-3" />}
                  Detected as {identifierType}
                </p>
              )}
              {error && (
                <p className="text-xs text-red-500">{error}</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full bg-secondary hover:bg-secondary/90"
              disabled={loading}
              data-testid="forgot-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-border text-center">
            <Link 
              to="/login" 
              className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              data-testid="forgot-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Sign In
            </Link>
          </div>
        </Card>

        {/* Help text */}
        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground">
            You can use any of the following to recover your account:
          </p>
          <div className="flex items-center justify-center gap-4 mt-2">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Mail className="w-3 h-3" /> Email
            </span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <AtSign className="w-3 h-3" /> @handle
            </span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Phone className="w-3 h-3" /> Phone
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
